import { Fade } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import ArrowBackIcon from '@serko/zui-icons/es/ArrowBackIcon';
import Typography from '@serko/zui/es/Typography';
import classNames from 'classnames';
import React, { createRef, Dispatch, PureComponent, RefObject } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import Slider, { Settings } from 'react-slick';
import compose from 'recompose/compose';
import { Action } from 'redux';
import messages from '../../../intl/messages';
import { SegmentTypes } from '../../../models/itinerary';
import { AppState } from '../../../store/model';
import { isNew } from '../../../store/reducers/itinerary/selectors';
import { findSegment as findSegmentDetails } from '../../../store/reducers/segment-details/selectors';
import { findSegment as findSegmentOptions } from '../../../store/reducers/segment-options/selectors';
import {
  findSelectedOption,
  getOptionIdFromOption,
} from '../../../store/reducers/selected-options/selectors';
import { selectSegment } from '../../../store/reducers/selected-segment/actions';
import { EmptyState } from '../../lab/EmptyState';
import { spacings } from '../../lab/spacings';
import { SegmentIcon } from '../../SegmentIcon';
import { OptionMobile } from './Option/OptionMobile';

// tslint:disable-next-line:no-import-side-effect
import 'slick-carousel/slick/slick-theme.css';
// tslint:disable-next-line:no-import-side-effect
import 'slick-carousel/slick/slick.css';

interface OwnProps {
  segmentId: number;
}

interface StateProps {
  selectedOptionId: string;
  optionIds: string[];
  title: string;
  type: SegmentTypes;
  show: boolean;
}

interface DispatchProps {
  onSegmentDeselect(): void;
  onOptionSelect(
    segmentId: number,
    optionId: string,
    isRecommended: boolean,
  ): void;
  openRequestChangesDialog(): void;
}

interface State {
  currentOptionIndex: number;
}

interface ContentMobileProps
  extends OwnProps,
    StateProps,
    DispatchProps,
    WithStyles<typeof styles>,
    InjectedIntlProps {}

class ContentMobileBase extends PureComponent<ContentMobileProps, State> {
  private slickSettings: Partial<Settings> = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    className: 'slick-slider-custom',
    adaptiveHeight: true,
  };

  private sliderRef: RefObject<Slider> = createRef<Slider>();

  constructor(props: ContentMobileProps) {
    super(props);
    this.state = {
      currentOptionIndex: props.optionIds.indexOf(props.selectedOptionId),
    };
  }

  public render() {
    const {
      classes,
      intl: { formatMessage },
      title,
      type,
      selectedOptionId,
      onSegmentDeselect,
      show,
      optionIds,
      openRequestChangesDialog,
    } = this.props;

    const { currentOptionIndex } = this.state;

    const isLastSlide =
      currentOptionIndex === (show ? optionIds.length : optionIds.length - 1);

    return (
      <Grid
        container
        className={classNames(
          classes.container,
          classes[`type${type}` as TypeClassKey],
        )}
        alignItems="stretch"
        direction="column"
        wrap="nowrap"
      >
        <Grid item>
          <ButtonBase
            className={classes.titleButton}
            onClick={onSegmentDeselect}
          >
            <div className={classes.titleContainer}>
              <Grid
                container
                alignItems="center"
                alignContent="center"
                spacing={16}
                wrap="nowrap"
              >
                <Grid item className={classes.arrowBackIcon}>
                  <ArrowBackIcon />
                </Grid>
                <Grid item xs zeroMinWidth>
                  <Typography
                    variant="title2"
                    color="inherit"
                    align="left"
                    lineClamp
                  >
                    {title}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </ButtonBase>
        </Grid>
        <Grid
          item
          container
          justify="space-between"
          spacing={8}
          alignItems="center"
          className={classes.optionTracker}
        >
          <Grid item>
            {show && (
              <IconButton
                className={classes.iconButton}
                disabled={currentOptionIndex === 0}
                onClick={this.goToPrevSlide}
              >
                <ChevronLeft />
              </IconButton>
            )}
          </Grid>
          <Grid item>
            {currentOptionIndex < optionIds.length && (
              <Typography variant="caption" color="textPrimary">
                {show &&
                  formatMessage(messages.segmentContent.optionOf, {
                    index: currentOptionIndex + 1,
                    optionCount: optionIds.length,
                  })}
              </Typography>
            )}
          </Grid>
          <Grid item>
            {show && (
              <IconButton
                className={classes.iconButton}
                disabled={currentOptionIndex === (show ? optionIds.length : 0)}
                onClick={this.goToNextSlide}
              >
                <ChevronRight />
              </IconButton>
            )}
          </Grid>
        </Grid>
        <Grid item className={classes.options}>
          <Slider
            swipe={show}
            ref={this.sliderRef}
            beforeChange={this.onBeforeChangeSlide}
            initialSlide={optionIds.indexOf(selectedOptionId)}
            {...this.slickSettings}
          >
            {this.renderOptions()}
            {!!show && (
              <EmptyState
                icon={<SegmentIcon type={type} />}
                heading={formatMessage(
                  messages.segmentContent.needMoreOptions.title,
                )}
                iconSize="sm"
                headingSize="sm"
                message={formatMessage(
                  messages.segmentContent.needMoreOptions.body,
                )}
                SecondaryActionProps={{
                  onClick: openRequestChangesDialog,
                  children: formatMessage(messages.common.requestChanges),
                }}
              />
            )}
          </Slider>
        </Grid>
        <Fade in={!isLastSlide}>
          <Grid item className={classes.selectButtonContainer}>
            <Button
              color="primary"
              variant="contained"
              onClick={this.onOptionSelect}
              disabled={isLastSlide}
              fullWidth
              className={classes.selectButton}
            >
              {formatMessage(messages.common.select)}
            </Button>
          </Grid>
        </Fade>
      </Grid>
    );
  }

  private goToNextSlide = () => {
    if (this.sliderRef.current) {
      this.sliderRef.current.slickNext();
    }
  };

  private goToPrevSlide = () => {
    if (this.sliderRef.current) {
      this.sliderRef.current.slickPrev();
    }
  };

  private onOptionSelect = () => {
    const {
      segmentId,
      optionIds,
      onOptionSelect,
      onSegmentDeselect,
    } = this.props;

    const { currentOptionIndex } = this.state;

    const isRecommended = currentOptionIndex === 0;

    onOptionSelect(segmentId, optionIds[currentOptionIndex], isRecommended);
    onSegmentDeselect();
  };

  private onFareToggle = () =>
    this.sliderRef.current && this.sliderRef.current.forceUpdate();

  private renderOption = (optionId: string, index: number) => {
    const { segmentId } = this.props;
    return (
      <OptionMobile
        key={index}
        index={index}
        isRecommended={index === 0}
        optionId={optionId}
        segmentId={segmentId}
        onFareToggle={this.onFareToggle}
      />
    );
  };

  private renderOptions = () => {
    const { optionIds } = this.props;

    return optionIds.map(this.renderOption);
  };

  private onBeforeChangeSlide = (_prev: number, next: number) => {
    this.setState({ currentOptionIndex: next });
  };
}

const mapStateToProps = (
  state: AppState,
  { segmentId }: ContentMobileProps,
): StateProps => {
  const selectedOption = findSelectedOption(segmentId)(state);
  const { optionIds } = findSegmentOptions(segmentId)(state);
  const { title, type } = findSegmentDetails(segmentId)(state);

  return {
    optionIds,
    selectedOptionId: getOptionIdFromOption(selectedOption)!,
    title,
    type,
    show: isNew(state),
  };
};

// styled
type TypeClassKey = 'typeAir' | 'typeCar' | 'typeHotel';

const styles = (theme: Theme) => {
  const {
    between: {
      card: { card: cardSpacing },
    },
  } = spacings(theme);

  const {
    spacing: { unit: spacing },
    zeno: {
      icons,
      text,
      travelModes: { air, accommodation, vehicle },
    },
  } = theme;

  return createStyles({
    titleButton: {
      width: '100%',
    },
    container: {
      minHeight: '100%',
      height: '100%',
    },
    sliderContainer: {
      flex: 'auto',
    },
    selectButtonContainer: {
      margin: spacing * 2,
      marginTop: 'auto',
    },
    selectButton: {
      marginTop: spacing * 2,
    },
    titleContainer: {
      background: air.main,
      display: 'flex',
      boxSizing: 'border-box',
      height: spacing * 7,
      color: text.light.main,
      paddingLeft: spacing * 3,
      paddingRight: spacing * 3,
      paddingTop: spacing * 2,
      paddingBottom: spacing * 2,
      width: '100%',
    },
    typeAir: {
      background: air.lightest,
      '& $titleContainer': {
        background: air.main,
      },
    },
    typeHotel: {
      background: accommodation.lightest,
      '& $titleContainer': {
        background: accommodation.main,
      },
    },
    typeCar: {
      background: vehicle.lightest,
      '& $titleContainer': {
        background: vehicle.main,
      },
    },
    arrowBackIcon: {
      color: icons.light.active,
      display: 'flex',
    },
    options: {
      ...cardSpacing,
    },
    optionTracker: {
      paddingLeft: spacing * 2,
      paddingRight: spacing * 2,
      ...cardSpacing,
    },
    iconButton: {
      color: icons.dark.active,
      height: spacing * 5,
      marginBottom: -2 * spacing,
      marginTop: -2 * spacing,
      marginLeft: -1 * spacing,
      marginRight: -1 * spacing,
      width: spacing * 5,
    },
    '@global': {
      '.slick-list': {
        flex: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'stretch',
        transition: 'height 0.2s ease-out',
        marginTop: -8,
        marginBottom: -8,
      },
      '.slick-track': {
        flex: 'auto',
      },
      '.slick-slide': {
        flex: 'auto',
        height: 'auto',
        boxSizing: 'border-box',
      },
      '.slick-slider-custom': {
        position: 'initial',
        flexDirection: 'column',
        flex: 'auto',
        display: 'flex',
        /**
         * space it a little to make sure the drop shadow
         * doesn't get cropped off by the slick's overflow:hidden on each slide
         */
        marginBottom: '-8px',
        marginTop: '-8px',
      },
    },
  });
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  onSegmentSelect: (id: string) => {
    dispatch(selectSegment(id));
  },
});

const enhance = compose<ContentMobileProps, OwnProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles),
  injectIntl,
);

export const ContentMobile = enhance(ContentMobileBase);
